import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const thisYear = new Date().getFullYear();
const DECADES = [];
for (let d = 1970; d <= thisYear; d += 10) DECADES.push(d);

const fmtSize = (n) => {
  if (!n) return "";
  const gb = n / 1073741824;
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${Math.round(n / 1048576)} MB`;
};

const key = (it) => `${it.kind}-${it.tmdb_id}`;

// Browse by decade: step 1 pick decades (+ movies or shows), step 2 live availability check against the
// torrent source (Prowlarr), then tick titles and add them all to All Requests with auto-approval.
export default function DecadeBrowser({ onAdded }) {
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState("movie");
  const [decades, setDecades] = useState({});
  const [perDecade, setPerDecade] = useState(40);
  const [job, setJob] = useState(null);
  const [picked, setPicked] = useState({});
  const [onlyAvail, setOnlyAvail] = useState(true);
  const [starting, setStarting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addProgress, setAddProgress] = useState(null);
  const [report, setReport] = useState(null);

  const chosen = DECADES.filter((d) => decades[d]);

  const start = async () => {
    setStarting(true); setReport(null); setPicked({});
    try {
      const { data } = await api.post("/discover/start", { kind, decades: chosen, per_decade: perDecade });
      setJob({ id: data.job_id, running: true, phase: "Starting", total: 0, checked: 0, items: [] });
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the search");
    } finally { setStarting(false); }
  };

  useEffect(() => {
    if (step !== 2 || !job?.id || !job.running) return;
    let stop = false;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/discover/${job.id}`);
        if (!stop) setJob(data);
      } catch (e) {
        // 404 = the server forgot the search (restart); stop polling instead of waiting forever
        if (e.response?.status === 404 && !stop) {
          setJob((j) => ({ ...j, running: false, phase: "Failed", error: "The search was lost (the server restarted). Start it again." }));
        }
      }
    }, 2000);
    return () => { stop = true; clearInterval(t); };
  }, [step, job?.id, job?.running]);

  const cancel = async () => { try { await api.post(`/discover/${job.id}/cancel`); } catch { /* ignore */ } };

  const items = job?.items || [];
  const shown = useMemo(
    () => items.filter((it) => !onlyAvail || (it.avail && it.avail.found > 0)),
    [items, onlyAvail],
  );
  const pickedItems = items.filter((it) => picked[key(it)]);

  const togglePick = (it) => setPicked((p) => ({ ...p, [key(it)]: !p[key(it)] }));
  const pickAllShown = () => {
    // Only titles that are actually available can be ticked, so "all on" is judged over those alone.
    const tickable = shown.filter((it) => it.avail && it.avail.found > 0);
    const allOn = tickable.length > 0 && tickable.every((it) => picked[key(it)]);
    setPicked((p) => {
      const n = { ...p };
      tickable.forEach((it) => { n[key(it)] = !allOn; });
      return n;
    });
  };

  // Adding runs as a background job on the server (paced, one title at a time); this polls it.
  const addSelected = async () => {
    if (!pickedItems.length) return;
    const k = pickedItems[0].kind;
    if (!window.confirm(`Add ${pickedItems.length} ${k === "movie" ? "movie" : "show"}${pickedItems.length === 1 ? "" : "s"} to All Requests and start downloading them?`)) return;
    setAdding(true); setReport(null);
    try {
      const { data: started } = await api.post("/discover/add", {
        items: pickedItems.map((it) => ({ kind: it.kind, tmdb_id: it.tmdb_id, title: it.title, year: it.year })),
      });
      setAddProgress({ done: 0, total: started.total });
      let misses = 0;
      for (;;) {
        await new Promise((r) => setTimeout(r, 2000));
        let j;
        try { ({ data: j } = await api.get(`/jobs/${started.job_id}`)); misses = 0; }
        catch (e) {
          if (e.response?.status === 404 || ++misses > 5) throw new Error("Lost track of the add job");
          continue;
        }
        setAddProgress({ done: j.done, total: j.total });
        if (!j.running) {
          const added = j.results.filter((r) => r.ok).length;
          const skipped = j.results.filter((r) => r.skipped).length;
          const failed = j.results.length - added - skipped;
          setReport({ added, skipped, failed, results: j.results });
          toast.success(`Added ${added}${skipped ? ` · ${skipped} already requested` : ""}${failed ? ` · ${failed} failed` : ""}`);
          setPicked({});
          onAdded && onAdded();
          break;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not add the titles");
    } finally { setAdding(false); setAddProgress(null); }
  };

  const btn = "text-[10px] uppercase tracking-[0.2em] border border-[#222] hover:border-[#93c5fd] hover:text-[#93c5fd] text-[#8A8A8A] px-3 py-2 disabled:opacity-50";

  return (
    <div className="mt-16" data-testid="decade-browser">
      <h2 className="font-display text-2xl font-bold tracking-tight text-white mb-1">Browse by decade (Admin)</h2>
      <p className="text-[#8A8A8A] text-sm mb-4 max-w-2xl">
        Pick one or more decades, check what the torrent source has right now, then add the ones you want to All Requests in one go (auto-approved, downloads start straight away).
      </p>

      {step === 1 && (
        <div className="border border-[#222] p-5 max-w-3xl">
          <div className="flex gap-2 mb-4">
            {[["movie", "Movies"], ["tv", "TV shows"]].map(([k, label]) => (
              <button key={k} onClick={() => setKind(k)}
                className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-2 ${kind === k ? "border-[#D9381E] text-white" : "border-[#222] text-[#8A8A8A] hover:text-white"}`}
                data-testid={`decade-kind-${k}`}>{label}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3" data-testid="decade-list">
            {DECADES.map((d) => (
              <label key={d} className={`flex items-center gap-2 border px-4 py-3 cursor-pointer select-none ${decades[d] ? "border-[#D9381E] text-white" : "border-[#222] text-[#8A8A8A] hover:text-white"}`}>
                <input type="checkbox" checked={!!decades[d]} onChange={() => setDecades((p) => ({ ...p, [d]: !p[d] }))}
                  className="accent-[#D9381E]" data-testid={`decade-${d}`} />
                {d}s
              </label>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <label className="text-xs text-[#8A8A8A] flex items-center gap-2">
              Most popular per decade
              <select value={perDecade} onChange={(e) => setPerDecade(Number(e.target.value))}
                className="bg-[#0A0A0A] border border-[#222] text-white text-xs px-2 py-2" data-testid="decade-count">
                {[20, 40, 60, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button onClick={start} disabled={!chosen.length || starting}
              className="bg-[#D9381E] hover:bg-[#ED4B32] text-white px-5 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50"
              data-testid="decade-search">{starting ? "Starting…" : `Check availability${chosen.length ? ` (${chosen.length} decade${chosen.length === 1 ? "" : "s"})` : ""} →`}</button>
          </div>
        </div>
      )}

      {step === 2 && job && (
        <div className="border border-[#222]" data-testid="decade-results">
          <div className="px-5 py-4 border-b border-[#222] flex items-center gap-4 flex-wrap">
            <button onClick={() => { if (job.running) cancel(); setStep(1); }} className={btn} data-testid="decade-back">← Decades</button>
            <div className="text-sm text-white">
              {job.error ? <span className="text-[#fca5a5]">{job.error}</span> : job.phase}
              {job.total > 0 && <span className="text-[#8A8A8A]"> · checked {job.checked}/{job.total}</span>}
              {items.length > 0 && <span className="text-[#8A8A8A]"> · {items.filter((i) => i.avail && i.avail.found > 0).length} available</span>}
            </div>
            {job.running && <button onClick={cancel} className={btn}>Stop</button>}
            <label className="ml-auto text-xs text-[#8A8A8A] flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={onlyAvail} onChange={(e) => setOnlyAvail(e.target.checked)} className="accent-[#D9381E]" />
              Only show available
            </label>
            <button onClick={pickAllShown} className={btn} data-testid="decade-pick-all">Tick / untick all shown</button>
            <button onClick={addSelected} disabled={!pickedItems.length || adding}
              className="bg-[#D9381E] hover:bg-[#ED4B32] text-white px-4 py-2 text-xs uppercase tracking-[0.2em] disabled:opacity-50"
              data-testid="decade-add">{adding ? `Adding… ${addProgress ? `${addProgress.done}/${addProgress.total}` : ""}` : `Add ${pickedItems.length} to All Requests`}</button>
          </div>

          {report && (
            <div className="px-5 py-3 border-b border-[#222] text-xs text-[#8A8A8A]" data-testid="decade-report">
              Added {report.added} · already requested {report.skipped} · failed {report.failed}
              {report.results.filter((r) => !r.ok).slice(0, 8).map((r, i) => (
                <div key={i} className="mt-1">{r.title}{r.year ? ` (${r.year})` : ""}: {r.error}</div>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <p className="text-[#8A8A8A] text-sm px-5 py-6">{job.running ? "Searching…" : "Nothing available in these decades."}</p>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {shown.map((it) => {
                const a = it.avail;
                const ok = a && a.found > 0;
                return (
                  <label key={key(it)} className={`flex items-center gap-4 px-5 py-3 border-b border-[#222] last:border-b-0 ${ok ? "cursor-pointer hover:bg-[#111]" : "opacity-60"}`}>
                    <input type="checkbox" disabled={!ok} checked={!!picked[key(it)]} onChange={() => togglePick(it)}
                      className="accent-[#D9381E]" data-testid={`decade-pick-${key(it)}`} />
                    {it.poster_url ? <img src={it.poster_url} alt="" className="w-9 h-14 object-cover bg-[#1A1A1A] shrink-0" /> : <div className="w-9 h-14 bg-[#1A1A1A] shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-white truncate">{it.title} {it.year ? <span className="text-[#8A8A8A]">({it.year})</span> : null}
                        {it.rating ? <span className="text-[#8A8A8A] text-xs"> · ★ {it.rating}</span> : null}</p>
                      <p className="text-xs mt-1 text-[#8A8A8A] truncate" title={a?.best || ""}>
                        {a === null ? "Checking…"
                          : a.error ? <span className="text-[#fca5a5]">Lookup failed: {a.error}</span>
                          : ok ? <><span className="text-[#86efac]">{a.found} release{a.found === 1 ? "" : "s"}</span> · best {a.seeders} seeders · {fmtSize(a.size)} · {a.best}</>
                          : "Not available right now"}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#666] shrink-0">{it.decade}s</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
